import React, { useEffect } from 'react'
import Card from '../../components/common/Card'
import Kanban from '../../components/common/Kanban/Kanban'
import PageMeta from '../../components/common/PageMeta'
import DefaultTabs from '../../components/common/Tabs'
import DefaultTable from '../../components/tables/DefaultTable/DefaultTable'
import { PipelineType, useBusiness } from '../../context/BusinessContext'
import { FilterType } from '../../context/CommonContext'
import { useCreate } from '../../context/CreateRegisterContext'
import BusinessAddButton from './BusinessAddButton'
import BusinessEmpty from './BusinessEmpty'
import BusinessFilterButton from './BusinessFilterButton'
import BusinessForm from './BusinessForm'
import BusinessModeView from './BusinessModeView'
import BusinessOptions from './BusinessOptions'
import BusinessCreateStep from './Create/BusinessCreateStep'
import PipelineCreate from './Create/PipelineCreate'
import BusinessDetail from './Detail/BusinessDetail'
import BusinessDeleteStep from './Edit/BusinessDeleteStep'
import BusinessEditStep from './Edit/BusinessEditStep'
import PipelineDelete from './Edit/PipelineDelete'
import PipelineEdit from './Edit/PipelineEdit'
import FilterEmpty from './FilterEmpty'
import BusinessDrawerFilter from './Filters/BusinessDrawerFilter'
import { useParams } from 'react-router'

const Business = () => {
    const { id } = useParams()
    const {
        filteredBusiness,
        openDetail,
        setOpenDetail,
        setCurrentStep,
        currentTab,
        setCurrentTab,
        currentStep,
        mode,
        openFilter,
        totalFilters,
        setOpenFilter
    } = useBusiness()
    const [openCreatePipeline, setOpenCreatePipeline] = React.useState(false)
    const [openEditPipeline, setOpenEditPipeline] = React.useState(false)
    const [openCreateStep, setOpenCreateStep] = React.useState(false)
    const [openEditStep, setOpenEditStep] = React.useState(false)
    const [openDeleteStep, setOpenDeleteStep] = React.useState(false)
    const [openDeletePipeline, setOpenDeletePipeline] = React.useState(false)
    const { openBusiness, setOpenBusiness } = useCreate()

    const handleBusinessConclude = async (businessID: number, values: any) => {
        setOpenBusiness(false)
        setOpenDetail({
            open: true,
            data: {
                ...values,
                id: businessID,
                title: values.name
            }
        })
    }

    //* Pipeline view
    const pipelineTabsData = filteredBusiness?.pipelines?.length
        ? filteredBusiness.pipelines.map((tab: PipelineType) => {
              return {
                  label: (
                      <div className='flex items-start justify-center gap-1'>
                          <p>{tab.label}</p>
                          <p className='text-xs text-brand-400 font-bold'>
                              {tab.steps.reduce((acc, step) => acc + step.business.length, 0)}
                          </p>
                      </div>
                  ),
                  value: tab.value,
                  content: (
                      <Kanban
                          key={tab.value}
                          columns={tab.steps.map(step => ({
                              id: step.id.toString(),
                              title: step.title,
                              color: step.color || '#000'
                          }))}
                          initialTasks={tab.steps.flatMap(step =>
                              step.business.map(biz => ({
                                  id: biz.id.toString(),
                                  title: biz.title,
                                  subtitle: biz.subtitle,
                                  subtitle2: biz.value,
                                  products: biz.products,
                                  targets: biz.targets,
                                  stepID: step.id.toString(),
                                  dateNotify: biz.dateNotify,
                                  isExpired: biz.isExpired,
                                  professionalID: biz.professionalID
                              }))
                          )}
                          tab={tab.value}
                          handleCreateStep={() => setOpenCreateStep(true)}
                          handleEditStep={() => setOpenEditStep(true)}
                          handleDeleteStep={() => setOpenDeleteStep(true)}
                      />
                  )
              }
          })
        : []

    //? List view
    const listTabsData = filteredBusiness?.pipelines?.length
        ? filteredBusiness.pipelines.map((tab: PipelineType) => ({
              label: (
                  <div className='flex items-start justify-center gap-1'>
                      <p>{tab.label}</p>
                      <p className='text-xs text-brand-400 font-bold'>
                          {tab.steps.reduce((acc, step) => acc + step.business.length, 0)}
                      </p>
                  </div>
              ),
              value: tab.value,
              content: (
                  <Card>
                      <DefaultTable
                          columns={[
                              { key: 'title', label: 'Título' },
                              { key: 'subtitle', label: 'Subtítulo' },
                              { key: 'value', label: 'Valor' },
                              { key: 'target', label: 'Etiquetas' },
                              { key: 'step', label: 'Etapa' }
                          ]}
                          rows={tab.steps.flatMap((step, stepIndex, stepsArray) =>
                              step.business.map(biz => {
                                  // Índice do step atual no pipeline
                                  const stepPosition = stepIndex + 1
                                  const totalSteps = stepsArray.length
                                  const progress = (stepPosition / totalSteps) * 100

                                  return {
                                      id: biz.id,
                                      status: `step-${biz.id}`,
                                      title: biz.title,
                                      subtitle: biz.subtitle || '--',
                                      value: biz.value || '--',
                                      target: biz.targets ? (
                                          <div className='flex gap-1'>
                                              {biz.targets.map((target: FilterType, index: number) => {
                                                  return (
                                                      <span
                                                          key={index}
                                                          className='text-[0.65rem] px-2 py-0.5 rounded-lg text-center'
                                                          style={{
                                                              border: `1px solid ${target.color}`,
                                                              color: target.color
                                                          }}
                                                      >
                                                          {target.label}
                                                      </span>
                                                  )
                                              })}
                                          </div>
                                      ) : (
                                          '--'
                                      ),
                                      step: (
                                          <div className='flex items-center gap-2'>
                                              <p>{step.title}</p>
                                              <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5'>
                                                  <div
                                                      className='h-2.5 rounded-full'
                                                      style={{
                                                          width: `${progress}%`,
                                                          backgroundColor: step.color
                                                      }}
                                                  ></div>
                                              </div>
                                          </div>
                                      )
                                  }
                              })
                          )}
                          rowsPerPage={20}
                          handleBusinessDetail
                      />
                  </Card>
              )
          }))
        : []

    //* Mode view (pipeline or list)
    const businessModeView = mode === 'pipeline' ? pipelineTabsData : listTabsData

    //? Verifica se há id na rota, se sim, abre negócio
    useEffect(() => {
        if (!id || id === undefined) return

        const task = filteredBusiness.pipelines
            .flatMap(pipeline => pipeline.steps)
            .flatMap(step => step.business)
            .find(biz => Number(biz.id) === Number(id))

        if (!task) return

        setOpenDetail({ open: true, data: task })
    }, [])

    return (
        <>
            <PageMeta title='Negócios | CRM' description='Kanban de negócios' />
            <DefaultTabs
                // key={Math.random()}
                key={mode + filteredBusiness.pipelines?.length}
                tabs={businessModeView}
                defaultValue={currentTab}
                componentOptions={
                    filteredBusiness.pipelines && filteredBusiness.pipelines.length > 0 ? (
                        <div className='flex items-center justify-between gap-2 w-full'>
                            <BusinessOptions
                                handleCreate={() => setOpenCreatePipeline(true)}
                                handleEdit={() => setOpenEditPipeline(true)}
                                handleCreateStep={() => {
                                    setCurrentStep(null)
                                    setOpenCreateStep(true)
                                }}
                                handleDeletePipeline={() => setOpenDeletePipeline(true)}
                            />
                            <div className='flex items-center gap-2'>
                                <BusinessModeView />
                                <BusinessFilterButton />
                                <BusinessAddButton />
                            </div>
                        </div>
                    ) : totalFilters > 0 ? (
                        <FilterEmpty />
                    ) : (
                        <BusinessEmpty handleCreate={() => setOpenCreatePipeline(true)} />
                    )
                }
                handleClick={setCurrentTab}
            />
            <PipelineCreate open={openCreatePipeline} handleClose={() => setOpenCreatePipeline(false)} />
            <PipelineEdit open={openEditPipeline} handleClose={() => setOpenEditPipeline(false)} />
            <PipelineDelete open={openDeletePipeline} handleClose={() => setOpenDeletePipeline(false)} />
            <BusinessCreateStep open={openCreateStep} handleClose={() => setOpenCreateStep(false)} />
            <BusinessEditStep open={openEditStep} handleClose={() => setOpenEditStep(false)} />
            <BusinessDeleteStep open={openDeleteStep} handleClose={() => setOpenDeleteStep(false)} />
            <BusinessForm
                type='create'
                open={openBusiness}
                handleClose={() => setOpenBusiness(false)}
                handleConclude={handleBusinessConclude}
                pipeline={businessModeView.find((tab: any) => tab.value === currentTab)}
                step={currentStep}
                data={null}
            />
            <BusinessDetail open={openDetail.open} handleClose={() => setOpenDetail({ open: false, data: null })} />
            <BusinessDrawerFilter open={openFilter} handleClose={() => setOpenFilter(false)} />
        </>
    )
}

export default Business
